import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  // Current data
  currentItems: number;
  totalItems: number;

  // Pagination state (optional - for page-based pagination)
  currentPage?: number;
  totalPages?: number;

  // Loading state
  isLoading?: boolean;

  // Navigation handlers
  onPrevious?: () => void;
  onNext?: () => void;

  // Disabled states
  canGoNext?: boolean;
  canGoPrevious?: boolean;

  // Customization
  itemName?: string; // e.g., "personas", "tasks", "scenarios"
  showPageNumbers?: boolean; // whether to show "Page X of Y"
  mode?: 'infinite' | 'pages'; // infinite shows "Load More", pages shows prev/next
  className?: string;
  // size of the "Showing x of y" label; the ontology board passes its compact size
  labelClassName?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentItems,
  totalItems,
  currentPage,
  totalPages,
  isLoading = false,
  onPrevious,
  onNext,
  canGoNext = true,
  canGoPrevious = true,
  itemName = "items",
  showPageNumbers = false,
  mode = 'infinite',
  className = "",
  labelClassName = "text-sm",
}) => {
  // Don't render if there are no items
  if (currentItems === 0 && totalItems === 0) {
    return null;
  }

  const isInfiniteMode = mode === 'infinite';

  return (
    <div className={`flex items-center justify-between px-2 py-1 bg-surface-card rounded ${className}`}>
      <span className={`${labelClassName} text-muted`}>
        {showPageNumbers && currentPage && totalPages ? (
          <>
            {currentPage} / {totalPages || 1}
            {totalItems > 0 && ` • ${totalItems} ${itemName}`}
          </>
        ) : (
          <>
            {currentItems} of {totalItems} {itemName}
            {isLoading && " …"}
          </>
        )}
      </span>

      <div className="flex items-center gap-1">
        {!isInfiniteMode && onPrevious && (
          <Button
            variant="outline"
            size="sm"
            onClick={onPrevious}
            disabled={!canGoPrevious || isLoading}
            aria-label="Previous page"
            title="Previous page"
            className="h-6 w-6 p-0 bg-surface-active border-surface-border text-text-primary hover:bg-surface-hover disabled:opacity-50"
          >
            <ChevronLeft className="w-3 h-3" />
          </Button>
        )}

        {onNext && (
          <Button
            variant="outline"
            size="sm"
            onClick={onNext}
            disabled={!canGoNext || isLoading}
            aria-label={isInfiniteMode ? "Load more" : "Next page"}
            title={isInfiniteMode ? "Load more" : "Next page"}
            className="h-6 w-6 p-0 bg-surface-active border-surface-border text-text-primary hover:bg-surface-hover disabled:opacity-50"
          >
            <ChevronRight className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
};